import { useMemo, useState } from "react";
import { Button, ButtonGroup, Pagination } from "@shopify/polaris";
import { GettingStartedStoreAccess } from "../../libs/store";
import { SubscriptionProgramStep } from "../../libs/GettingStartedChecklist/SubscriptionProgramStep";
import { CreateSubscriptionModal, useCreateSubscriptionModalStore } from "./CreateSubscriptionModal";
import SetupStep from "../SetupStep";
import { SelectProductsButton } from "./CreateSubscriptionModal/SelectProductsButton";
import { BrowseProductsModal } from "@vendor-app/app/_sharedComponents/BrowseProductsModal";
import { useSmartrrVendorSelector } from "@vendor-app/app/_state/typedVendorReduxHooks";
import { MAX_PAGE_SIZE } from "@smartrr/shared/utils/paginatedQuery";
import { PaginationContainer } from "@vendor-app/app/AdminRoute/AdminSubscriptionDetailsRoute/libs";

import { usePurchasableOptions } from "../../libs/hooks/usePurchasableOptions";
import { useProductVariantSelection } from "../../libs/hooks/useProductVariantSelection";
import { useEditSellingPlanProducts } from "../../libs/hooks/useEditSellingPlanProducts";
import { useLocation } from "@vendor-app/utils/useLocation";

export default function SubscriptionProgramSetup() {
  const setup = GettingStartedStoreAccess.useSetup();
  const checklist = GettingStartedStoreAccess.useChecklist();
  const isLoading = GettingStartedStoreAccess.useLoading("subscriptionProgram");
  const sellingPlanGroup = GettingStartedStoreAccess.useSellingPlanGroup();
  const [isAddingProducts, setIsAddingProducts] = useState(false);

  const { purchasables } = useSmartrrVendorSelector(s => s.purchasables);
  const location = useLocation();

  const [searchText, setSearchText] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(0);

  const { openModal } = useCreateSubscriptionModalStore();

  const initialSelectionIds = useMemo(() => {
    return [...sellingPlanGroup.productIds, ...sellingPlanGroup.variantIds];
  }, [sellingPlanGroup.productIds, sellingPlanGroup.variantIds]);

  const isStepCompleted = Boolean(setup?.subscriptionSetup.sellingPlan.completed && initialSelectionIds.length);

  const isReturningFromProgramCreation = location.hash === "fromSubscriptionProgram";

  const isOpen =
    (isReturningFromProgramCreation && !isStepCompleted) || checklist?.isOpen(SubscriptionProgramStep) === true;

  const purchasableOptions = usePurchasableOptions({ purchasables, searchText });

  const allIds = useMemo(() => {
    return purchasables.flatMap(p => [
      ...(p.shopifyId && !p.isDraftOrArchived ? [p.shopifyId] : []),
      ...(p.vnts ?? []).filter(v => v.shopifyId && !v.isDraftOrArchived).map(v => v.shopifyId),
    ]);
  }, [purchasables]);

  const {
    selectedIds,
    setSelectedIds,
    reset: resetSelection,
    toggleAll,
  } = useProductVariantSelection(initialSelectionIds);

  // There is a check for selectedSellingPlanId in <SelectProductsButton>
  const updateProducts = useEditSellingPlanProducts(setup?.selectedSellingPlanId!);

  const handleConfirm = async () => {
    setIsAddingProducts(true);
    const updatesSellingPlanProducts = await updateProducts(selectedIds);

    if (updatesSellingPlanProducts) {
      setShowModal(false);
    }
    setIsAddingProducts(false);
  };

  return (
    <SetupStep
      title="Create Subscription Program"
      description="Set up your first plan to start offering subscriptions."
      isStepCompleted={isStepCompleted}
      id="subscription-program-setup"
      statusBadgeTestId="create-program-status-badge"
      isOpen={isOpen}
    >
      <ButtonGroup>
        <Button primary onClick={() => openModal({})}>
          Create my own program
        </Button>
        <SelectProductsButton setShowProductOptionsModal={setShowModal} />
      </ButtonGroup>
      <CreateSubscriptionModal />

      <BrowseProductsModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedIds([...sellingPlanGroup.productIds, ...sellingPlanGroup.variantIds]);
        }}
        title="Select the existing product and variants"
        purchasableOptions={purchasableOptions.slice(page * MAX_PAGE_SIZE, (page + 1) * MAX_PAGE_SIZE)}
        searchText={searchText}
        selectedProductsAndVariants={selectedIds}
        setSelectedProductAndVariantIds={setSelectedIds}
        onSearchChange={val => {
          setSearchText(val);
          setPage(0);
        }}
        primaryAction={{
          content: "Confirm Products/Variants",
          onAction: handleConfirm,
          loading: isAddingProducts || isLoading,
          disabled: !selectedIds.length,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction() {
              setShowModal(false);
              resetSelection([...sellingPlanGroup.productIds, ...sellingPlanGroup.variantIds]);
            },
          },
        ]}
        searchSuffix={
          !searchText && (
            <Button onClick={() => toggleAll(allIds)} plain>
              {selectedIds.length === allIds.length ? "Deselect all" : "Select all"}
            </Button>
          )
        }
        footer={
          <PaginationContainer>
            <Pagination
              hasNext={page + 1 < Math.ceil(purchasableOptions.length / MAX_PAGE_SIZE)}
              hasPrevious={page > 0}
              onNext={() => setPage(p => p + 1)}
              onPrevious={() => setPage(p => p - 1)}
              label={`Showing ${page + 1} of ${Math.ceil(purchasableOptions.length / MAX_PAGE_SIZE)}`}
            />
          </PaginationContainer>
        }
      />
    </SetupStep>
  );
}
